import type { InventoryRow } from '@/infrastructure/FulfillmentAdminClient';

/** One sku's stock on the fulfillment book, as the scan desk shows it beside an order line (SSD-7521). */
export interface SkuStock {
  onHand: number;
  available: number;
}

/**
 * Each sku summed across every shelf it sits on, the way Current Stocks and the inbound request form sum it:
 * on hand is every unit on the book, available is on hand less what orders already hold.
 */
export const stockBySku = (rows: readonly InventoryRow[]): Record<string, SkuStock> => {
  const totals: Record<string, { onHand: number; reserved: number }> = {};
  for (const row of rows) {
    const total = (totals[row.sku] ??= { onHand: 0, reserved: 0 });
    total.onHand += row.onHand;
    total.reserved += row.reserved;
  }
  return Object.fromEntries(
    Object.entries(totals).map(([sku, total]) => [
      sku,
      { onHand: total.onHand, available: total.onHand - total.reserved },
    ]),
  );
};

/** The distinct skus of an order's lines, sorted, so the same order reads the same key. */
export const orderSkus = (lines: ReadonlyArray<{ sku: string | null | undefined }>): string[] =>
  [...new Set(lines.map((line) => (line.sku ?? '').trim()).filter((sku) => sku !== ''))].sort();
